import { Mark, type Node as ProseMirrorNode } from '@tiptap/pm/model';
import { type Diff } from 'diff-match-patch';
import { DiffMatchPatch } from 'diff-match-patch-ts';

const dmp = new DiffMatchPatch();
const WORD_DIFF_TOKEN = /\s+|[\p{L}\p{N}_]+|[^\s\p{L}\p{N}_]+/gu;
const MAX_SINGLE_CODE_UNIT_TOKENS = 0xd7ff;

export type DocumentDiffGranularity = 'character' | 'word';

// Like diff_linesToChars, encode unique tokens as non-surrogate UTF-16 code units to preserve them on decode; whitespace code units can make semantic cleanup boundaries slightly suboptimal.
export function computeWordDiffs(
  before: string,
  after: string,
  maxTokens = MAX_SINGLE_CODE_UNIT_TOKENS
): Diff[] {
  const tokenIds = new Map<string, number>();
  const tokens: string[] = [];

  const encode = (text: string): string | null => {
    const encoded: string[] = [];
    for (const token of text.match(WORD_DIFF_TOKEN) ?? []) {
      let id = tokenIds.get(token);
      if (id === undefined) {
        id = tokens.length + 1;
        if (id > maxTokens) return null;
        tokenIds.set(token, id);
        tokens.push(token);
      }
      encoded.push(String.fromCharCode(id));
    }
    return encoded.join('');
  };

  const encodedBefore = encode(before);
  const encodedAfter = encode(after);
  if (encodedBefore === null || encodedAfter === null) {
    // Too many unique tokens for single-code-unit encoding: fall back to a correct character-level diff.
    const diffs = dmp.diff_main(before, after) as Diff[];
    dmp.diff_cleanupSemantic(diffs);
    return diffs;
  }

  const encodedDiffs = dmp.diff_main(encodedBefore, encodedAfter) as Diff[];
  dmp.diff_cleanupSemantic(encodedDiffs);
  return encodedDiffs.map(([operation, encoded]) => [
    operation,
    Array.from(encoded, (character) => tokens[character.charCodeAt(0) - 1]).join(''),
  ]);
}

type TextSegment = {
  textOffset: number;
  docPos: number;
  length: number;
};

type MarksEntry = {
  textOffset: number;
  length: number;
  marks: readonly Mark[];
};

export type DocumentDiffChange =
  | { type: 'insert' | 'format'; from: number; to: number }
  | { type: 'delete'; pos: number; text: string };

export function buildTextPositionMap(doc: ProseMirrorNode): {
  text: string;
  segments: TextSegment[];
} {
  const segments: TextSegment[] = [];
  let text = '';
  let blockCount = 0;

  doc.descendants((node, pos) => {
    if (node.isTextblock) {
      if (blockCount > 0) {
        segments.push({ textOffset: text.length, docPos: pos, length: 1 });
        text += '\n';
      }
      blockCount++;
    }
    if (node.isText && node.text) {
      segments.push({ textOffset: text.length, docPos: pos, length: node.text.length });
      text += node.text;
    }
    return true;
  });

  return { text, segments };
}

export function textOffsetToDocPos(segments: TextSegment[], offset: number): number {
  for (let index = segments.length - 1; index >= 0; index--) {
    const segment = segments[index]!;
    if (offset >= segment.textOffset) {
      return segment.docPos + Math.min(offset - segment.textOffset, segment.length);
    }
  }
  return 0;
}

function buildMarksLookup(doc: ProseMirrorNode): MarksEntry[] {
  const entries: MarksEntry[] = [];
  let textOffset = 0;
  let blockCount = 0;

  doc.descendants((node) => {
    if (node.isTextblock) {
      if (blockCount > 0) {
        entries.push({ textOffset, length: 1, marks: [] });
        textOffset++;
      }
      blockCount++;
    }
    if (node.isText && node.text) {
      entries.push({ textOffset, length: node.text.length, marks: node.marks });
      textOffset += node.text.length;
    }
    return true;
  });

  return entries;
}

function getMarksAtTextOffset(lookup: MarksEntry[], offset: number): readonly Mark[] {
  for (const entry of lookup) {
    if (offset >= entry.textOffset && offset < entry.textOffset + entry.length) {
      return entry.marks;
    }
  }
  return [];
}

export function computeDocumentDiffChanges(
  beforeDoc: ProseMirrorNode,
  afterDoc: ProseMirrorNode,
  granularity: DocumentDiffGranularity = 'character'
): DocumentDiffChange[] {
  const before = buildTextPositionMap(beforeDoc);
  const after = buildTextPositionMap(afterDoc);
  const beforeMarks = buildMarksLookup(beforeDoc);
  const afterMarks = buildMarksLookup(afterDoc);
  const diffs =
    granularity === 'word'
      ? computeWordDiffs(before.text, after.text)
      : (dmp.diff_main(before.text, after.text) as Diff[]);
  if (granularity === 'character') dmp.diff_cleanupSemantic(diffs);
  const changes: DocumentDiffChange[] = [];
  let beforeOffset = 0;
  let afterOffset = 0;

  for (const [operation, text] of diffs) {
    if (operation === 1) {
      changes.push({
        type: 'insert',
        from: textOffsetToDocPos(after.segments, afterOffset),
        to: textOffsetToDocPos(after.segments, afterOffset + text.length),
      });
      afterOffset += text.length;
      continue;
    }

    if (operation === -1) {
      changes.push({
        type: 'delete',
        pos: textOffsetToDocPos(after.segments, afterOffset),
        text,
      });
      beforeOffset += text.length;
      continue;
    }

    let formatStart: number | null = null;
    for (let index = 0; index < text.length; index++) {
      const marksChanged = !Mark.sameSet(
        getMarksAtTextOffset(beforeMarks, beforeOffset + index),
        getMarksAtTextOffset(afterMarks, afterOffset + index)
      );
      if (marksChanged && formatStart === null) {
        formatStart = afterOffset + index;
      } else if (!marksChanged && formatStart !== null) {
        changes.push({
          type: 'format',
          from: textOffsetToDocPos(after.segments, formatStart),
          to: textOffsetToDocPos(after.segments, afterOffset + index),
        });
        formatStart = null;
      }
    }
    if (formatStart !== null) {
      changes.push({
        type: 'format',
        from: textOffsetToDocPos(after.segments, formatStart),
        to: textOffsetToDocPos(after.segments, afterOffset + text.length),
      });
    }
    beforeOffset += text.length;
    afterOffset += text.length;
  }

  return changes;
}
